import React, { ComponentType, useState } from 'react';
import { connect } from 'react-redux';
import {
  ApplicationErrorPayload,
  setApplicationError as setApplicationErrorConnect,
} from '../../../stores/app-state-store';
import { setCredentials as setCredentialsConnect } from '../../../stores/credentials-store';
import { RefreshableItem } from '../../../models/refreshableItem';
import { applicationParameters } from '../../../utilities/application-parameters';
import { HandoffCredentialsAction } from '../../../models/handoffSession';
import {
  handoffFlowAvailable as handoffFlowAvailableImport,
  ProviderEntity,
} from '../../../models/provider';
import { useConfiguration } from '../../ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { AwaitingCredentialsAuth } from '../AwaitingCredentialsScreen/AwaitingCredentialsAuth';
import { AppState, ConnectedPropsWithoutDispatch } from '../../../stores/redux-store';
import { SubmitCredentialsScreen } from '../molecules/SubmitCredentialsScreen/SubmitCredentialsScreen';
import { useProduct } from '../../ApplicationProviders/ProductProvider/ProductProvider';
import { useVersion } from '../../ApplicationProviders/VersionProvider/VersionProvider';

import { CredentialsEntity } from '../../../models/credentials';
import { useCredentialsPoller } from '../../hooks/useCredentialsPoller/useCredentialsPoller';
import {
  HandoffCreatorMachineView as HandoffCreatorMachineViewImport,
  HandoffCreatorMachineViewProps,
} from '../../../connectivity/screens/handoff/HandoffCreatorMachineView';
import { BackButton } from '../../organisms/Header/HeaderMolecule';
import { SubmitCredentialsPayload } from './hooks/useSubmitCredentials/useSubmitCredentials';
import { PersistedProviderInputsProvider } from './PersistedProviderInputsProvider/PersistedProviderInputsProvider';

export type SubmitAuthCredentialsOwnProps = {
  userHintId?: string;
  provider: ProviderEntity;
  renderProviderFields: boolean;
  handoffCredentialsAction: HandoffCredentialsAction;
  refreshableItems: RefreshableItem[];
  onSubmitCredentials: (payload: SubmitCredentialsPayload) => void;
  HandoffCreatorMachineView?: ComponentType<HandoffCreatorMachineViewProps>;
  handoffFlowAvailable?: typeof handoffFlowAvailableImport;
  isLoginTemplateTestGroup?: boolean;
  location?: Location;
} & BackButton;

export const SubmitAuthCredentialsPresenter: React.FC<
  SubmitAuthCredentialsOwnProps & PropsFromRedux
> = ({
  userHintId,
  provider,
  renderProviderFields,
  handoffCredentialsAction,
  refreshableItems,
  onBack,
  onSubmitCredentials,
  user,
  session,
  HandoffCreatorMachineView = HandoffCreatorMachineViewImport,
  handoffFlowAvailable = handoffFlowAvailableImport,
  accessToken,
  location = window.location,
  setCredentials,
  setApplicationError,
}) => {
  const { productType, productDimension } = useProduct();
  const {
    version: { version },
  } = useVersion();
  const { origin } = location;
  const { signCredentials } = useCredentialsPoller();
  const trackingId = applicationParameters.get('trackingId');
  const [forcedWebFlow, setForcedWebFlow] = useState(false);
  const { data: configurationData } = useConfiguration();
  const username = applicationParameters.get('username');
  const personalIdentifier = session?.personalIdentifier;
  const isHandoffFlowAvailable = handoffFlowAvailable(provider, configurationData, user?.id);

  if (!forcedWebFlow && isHandoffFlowAvailable) {
    const onDone = (credentials: CredentialsEntity) => {
      setCredentials(credentials);
      signCredentials(credentials.id);
    };

    const onError = (error: ApplicationErrorPayload, credentials?: CredentialsEntity) => {
      if (credentials) {
        setCredentials(credentials);
      }
      setApplicationError(error);
    };

    return (
      <HandoffCreatorMachineView
        provider={provider}
        userHintId={userHintId}
        forceWebFlow={() => {
          onSubmitCredentials({ fields: {} });
          setForcedWebFlow(true);
        }}
        handoffCredentialsAction={handoffCredentialsAction}
        onBack={onBack}
        userAccessToken={accessToken}
        productType={productType}
        productDimension={productDimension}
        version={version}
        trackingId={trackingId}
        locationOrigin={origin}
        onDone={onDone}
        onError={onError}
      />
    );
  }

  return (
    <PersistedProviderInputsProvider
      providerFields={provider.fields}
      fixedValue={personalIdentifier || username || undefined}
    >
      <AwaitingCredentialsAuth provider={provider} onBack={onBack}>
        <SubmitCredentialsScreen
          onSubmitCredentials={onSubmitCredentials}
          userHintId={userHintId}
          onBack={onBack}
          renderProviderFields={renderProviderFields}
          provider={provider}
          user={user}
          refreshableItems={refreshableItems}
        />
      </AwaitingCredentialsAuth>
    </PersistedProviderInputsProvider>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    user: state.user.user,
    accessToken: state.user.accessToken,
    session: state.session.data,
  };
};

const mapDispatchToProps = {
  setCredentials: setCredentialsConnect,
  setApplicationError: setApplicationErrorConnect,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedPropsWithoutDispatch<typeof connector>;

export const SubmitAuthCredentials = connector(SubmitAuthCredentialsPresenter);
